import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import RouteSEO from "./components/RouteSEO.tsx";

// Lazy-load all non-index routes
const Manifesto = lazy(() => import("./pages/Manifesto.tsx"));
const FoundingMembers = lazy(() => import("./pages/FoundingMembers.tsx"));
const FoundingMembersFamily = lazy(() => import("./pages/FoundingMembersFamily.tsx"));
const NonlinearThinkers = lazy(() => import("./pages/NonlinearThinkers.tsx"));
const CrisisHunger = lazy(() => import("./pages/CrisisHunger.tsx"));
const CrisisHomelessness = lazy(() => import("./pages/CrisisHomelessness.tsx"));
const CrisisClimate = lazy(() => import("./pages/CrisisClimate.tsx"));
const CrisisEducation = lazy(() => import("./pages/CrisisEducation.tsx"));
const CrisisWellbeing = lazy(() => import("./pages/CrisisWellbeing.tsx"));
const CrisisLeadership = lazy(() => import("./pages/CrisisLeadership.tsx"));
const Events = lazy(() => import("./pages/Events.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const TOMExplained = lazy(() => import("./pages/TOMExplained.tsx"));
const WhyAnOctopus = lazy(() => import("./pages/WhyAnOctopus.tsx"));
const NonlinearThinking = lazy(() => import("./pages/NonlinearThinking.tsx"));
const HumanMycelium = lazy(() => import("./pages/HumanMycelium.tsx"));
const TellTOM = lazy(() => import("./pages/TellTOM.tsx"));
const Sustainability = lazy(() => import("./pages/Sustainability.tsx"));
const HumanRightsPolicy = lazy(() => import("./pages/HumanRightsPolicy.tsx"));
const PrivacyStatement = lazy(() => import("./pages/PrivacyStatement.tsx"));
const ThinkTank = lazy(() => import("./pages/ThinkTank.tsx"));
const Donate = lazy(() => import("./pages/Donate.tsx"));
const MycelialConnection = lazy(() => import("./pages/MycelialConnection.tsx"));
const Surprise = lazy(() => import("./pages/Surprise.tsx"));
const BecomePartner = lazy(() => import("./pages/BecomePartner.tsx"));
const KidsCouncil = lazy(() => import("./pages/KidsCouncil.tsx"));
const ProjectOctopus = lazy(() => import("./pages/ProjectOctopus.tsx"));
const ScienceMember = lazy(() => import("./pages/ScienceMember.tsx"));
const WallOfThoughts = lazy(() => import("./pages/WallOfThoughts.tsx"));
const WallAdmin = lazy(() => import("./pages/WallAdmin.tsx"));
const HumanScore = lazy(() => import("./pages/HumanScore.tsx"));
const Membership = lazy(() => import("./pages/Membership.tsx"));
const WisdomMembership = lazy(() => import("./pages/WisdomMembership.tsx"));
const OctopusAnonymous = lazy(() => import("./pages/OctopusAnonymous.tsx"));
const Stories = lazy(() => import("./pages/Stories.tsx"));
const StoriesAdmin = lazy(() => import("./pages/StoriesAdmin.tsx"));
const WhoHoldsTheSpace = lazy(() => import("./pages/WhoHoldsTheSpace.tsx"));
const Whatsapp = lazy(() => import("./pages/Whatsapp.tsx"));
const Shop = lazy(() => import("./pages/Shop.tsx"));
const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <RouteSEO />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/manifesto" element={<Manifesto />} />
            <Route path="/founding-members" element={<FoundingMembers />} />
            <Route path="/founding-members-family" element={<FoundingMembersFamily />} />
            <Route path="/nonlinear-thinkers" element={<NonlinearThinkers />} />
            <Route path="/crisis/hunger" element={<CrisisHunger />} />
            <Route path="/crisis/homelessness" element={<CrisisHomelessness />} />
            <Route path="/crisis/climate-change" element={<CrisisClimate />} />
            <Route path="/crisis/education" element={<CrisisEducation />} />
            <Route path="/crisis/well-being" element={<CrisisWellbeing />} />
            <Route path="/crisis/leadership" element={<CrisisLeadership />} />
            <Route path="/events" element={<Events />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/tom-explained" element={<TOMExplained />} />
            <Route path="/why-an-octopus" element={<WhyAnOctopus />} />
            <Route path="/nonlinear-thinking" element={<NonlinearThinking />} />
            <Route path="/human-mycelium" element={<HumanMycelium />} />
            <Route path="/telltom" element={<TellTOM />} />
            <Route path="/sustainability" element={<Sustainability />} />
            <Route path="/human-rights-policy" element={<HumanRightsPolicy />} />
            <Route path="/privacy-statement" element={<PrivacyStatement />} />
            <Route path="/think-tank" element={<ThinkTank />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/mycelial-connection" element={<MycelialConnection />} />
            <Route path="/surprise" element={<Surprise />} />
            <Route path="/become-a-partner" element={<BecomePartner />} />
            <Route path="/kids-council" element={<KidsCouncil />} />
            <Route path="/project-octopus" element={<ProjectOctopus />} />
            <Route path="/science-member" element={<ScienceMember />} />
            <Route path="/wall-of-thoughts" element={<WallOfThoughts />} />
            <Route path="/wall-admin" element={<WallAdmin />} />
            <Route path="/human-score" element={<HumanScore />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/wisdom-membership" element={<WisdomMembership />} />
            <Route path="/octopus-anonymous" element={<OctopusAnonymous />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/stories-admin" element={<StoriesAdmin />} />
            <Route path="/who-holds-the-space" element={<WhoHoldsTheSpace />} />
            <Route path="/whatsapp" element={<Whatsapp />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/product/:handle" element={<ProductDetail />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
